import React from 'react';
import { 
  Grid, Box, Typography, Avatar, Divider, List, ListItem, ListItemText
} from '@mui/material';
import { 
  Timeline, TimelineItem, TimelineSeparator, TimelineConnector, TimelineContent, TimelineDot 
} from '@mui/lab';
// --- Icons ---
import SchoolIcon from '@mui/icons-material/School';
import WorkIcon from '@mui/icons-material/Work';
import CircleIcon from '@mui/icons-material/Circle';
import SquareIcon from '@mui/icons-material/Square';

// --- 1. RESUME DATA ---
const resumeData = {
  personalInfo: {
    name: "RICHARD SANCHEZ",
    title: "Marketing Manager",
    email: "hello@reallygreatsite.com",
    phone: "+123-456-7890",
    address: "123 Anywhere St., Any City",
    profileSummary: "I'm a passionate and results-oriented digital marketing strategist with a proven track record of unlocking growth and amplifying brand awareness for small businesses.",
  },
  skills: [
    "Project Management", "Public Relations", "Teamwork", "Time Management", 
    "Leadership", "Effective Communication", "Critical Thinking"
  ],
  languages: [
    { name: "English", proficiency: "(Fluent)" },
    { name: "French", proficiency: "(Fluent)" },
    { name: "German", proficiency: "(Basics)" },
  ],
  reference: {
    heading: "REFERENCE",
    name: "Harper Russo",
    title: "Wardiere Inc. / CEO",
    phone: "123-456-7890",
    email: "hello@reallygreatsite.com"
  },
  education: [
    {
      year: "2036",
      degree: "Master of Business Management",
      institution: "Wardiere University",
      details: "Graduated with highest honors, recognizing academic excellence.",
      gpa: "3.75/4.00"
    },
    {
      year: "2032",
      degree: "Bachelor of Business Management",
      institution: "Wardiere University",
      details: "Graduated with highest honors, recognizing academic excellence.",
      gpa: "3.75/4.00"
    }
  ],
  experience: [
    {
      year: "NOW",
      yearStart: "2032",
      title: "Marketing Manager",
      company: "Borcelle Studio",
      bulletPoints: [
        "Develop and implement comprehensive marketing strategies aligned with overall business objectives.",
        "Analyze market trends and competitor activities to identify opportunities for growth.",
      ]
    },
    {
      year: "2032",
      yearStart: "2028",
      title: "Creative Director",
      company: "Borcelle Studio",
      bulletPoints: [
        "Plan, execute, and optimize multi-channel marketing campaigns to drive brand visibility.",
        "Develop and implement comprehensive marketing strategies aligned with overall business objectives.",
      ]
    }
  ]
};

// --- 3. REUSABLE COMPONENTS ---
const CustomTimelineDot = ({ icon: Icon, color }) => (
  <Box sx={{ 
    display: 'inline-flex', 
    alignItems: 'center', 
    justifyContent: 'center', 
    width: 30, 
    height: 30, 
    borderRadius: '50%', 
    bgcolor: color,
    mr: 1 
  }}>
    <Icon sx={{ color: 'white', fontSize: 18 }} />
  </Box>
);

const LeftColumnList = ({ heading, items, useBullets = true }) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="h6" sx={{ 
      fontWeight: 'bold', 
      mb: 1, 
      textTransform: 'uppercase', 
      fontSize: '1rem' 
    }}>
      {heading}
    </Typography>
    <List disablePadding dense sx={{ ml: -1 }}>
      {items.map((item, index) => (
        <ListItem key={index} sx={{ py: 0, pl: 0.5 }}>
          {useBullets ? (
            <SquareIcon sx={{ fontSize: 7, mr: 1, color: 'white' }} />
          ) : (
            <Box sx={{ width: 14 }} /> 
          )}
          <ListItemText 
            primary={typeof item === 'string' ? item : `${item.name} ${item.proficiency}`}
            primaryTypographyProps={{ 
              color: 'white', 
              fontSize: '0.9rem',
              lineHeight: 1.2
            }}
          />
        </ListItem>
      ))}
    </List>
  </Box>
);

// --- 4. MAIN RESUME COMPONENT ---
const Resume23 = ({ 
  color = '#e91e63'
}) => {
  const data = resumeData;

  // Use the single color for all color properties
  const sidebarColor = color;
  const accentColor = color;
  const textColor = '#666';

  return (
    <Box sx={{ 
      width: "210mm",
      height: "297mm",
      boxShadow: 3, 
      borderRadius: '8px', 
      overflow: 'hidden',
      display: 'flex',
      bgcolor: 'white'
    }}>
      <Grid container sx={{ height: '100%' }}>
        
        {/* === LEFT COLUMN (Dark Blue) === */}
        <Grid item xs={12} sm={4} sx={{ width: '30%' }}>
          <Box sx={{
            backgroundColor: sidebarColor,
            color: 'white',
            p: 4,
            height: '100%',
            pb: 4 
          }}>
            
            {/* Profile Photo */}
            <Avatar 
              alt={data.personalInfo.name} 
              src={data.personalInfo.photoUrl}
              sx={{ 
                width: 140, 
                height: 140, 
                mb: 3, 
                border: '4px solid white', 
                mx: 'auto'
              }} 
            />

            {/* Profile Info */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ 
                fontWeight: 'bold', 
                mb: 1, 
                textTransform: 'uppercase', 
                fontSize: '1rem' 
              }}>
                PROFILE INFO
              </Typography>
              <Typography variant="body2" sx={{ 
                color: 'lightgray', 
                fontSize: '0.85rem' 
              }}>
                {data.personalInfo.profileSummary}
              </Typography>
            </Box>
            
            {/* Skills */}
            <LeftColumnList heading="SKILLS" items={data.skills} />
            
            {/* Reference */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ 
                fontWeight: 'bold', 
                mb: 1, 
                textTransform: 'uppercase', 
                fontSize: '1rem' 
              }}>
                {data.reference.heading}
              </Typography>
              <Typography variant="body2" sx={{ 
                fontWeight: 'bold', 
                fontSize: '0.9rem' 
              }}>
                {data.reference.name}
              </Typography>
              <Typography variant="body2" sx={{ 
                color: 'lightgray', 
                fontSize: '0.8rem' 
              }}>
                {data.reference.title}
              </Typography>
              <Typography variant="body2" sx={{ 
                color: 'lightgray', 
                fontSize: '0.8rem' 
              }}>
                Phone: {data.reference.phone}
              </Typography>
              <Typography variant="body2" sx={{ 
                color: 'lightgray', 
                fontSize: '0.8rem' 
              }}>
                Email: {data.reference.email}
              </Typography>
            </Box>

            {/* Languages */}
            <LeftColumnList heading="LANGUAGES" items={data.languages} useBullets={false} />

          </Box>
        </Grid>
        
        {/* === RIGHT COLUMN (White) === */}
        <Grid item xs={12} sm={8} sx={{ width: '70%' }}>
          <Box sx={{ p: 4, height: '100%', overflow: 'auto' }}>
            
            {/* Name & Contact Info */}
            <Typography variant="h3" sx={{ 
              fontWeight: '900', 
              color: accentColor,
              lineHeight: 1.1,
              fontSize: '2.5rem'
            }}>
              {data.personalInfo.name}
            </Typography>
            <Typography variant="h6" sx={{ 
              color: textColor,
              mb: 2, 
              fontSize: '1.2rem' 
            }}>
              {data.personalInfo.title}
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              fontSize: '0.85rem', 
              color: textColor,
              mb: 2,
              gap: 1
            }}>
              <Typography sx={{ fontSize: '0.85rem' }}>{data.personalInfo.email}</Typography>
              <Typography sx={{ fontSize: '0.85rem' }}>|</Typography>
              <Typography sx={{ fontSize: '0.85rem' }}>{data.personalInfo.phone}</Typography>
              <Typography sx={{ fontSize: '0.85rem' }}>|</Typography>
              <Typography sx={{ fontSize: '0.85rem' }}>{data.personalInfo.address}</Typography>
            </Box>
            
            {/* Education */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 'bold', 
                color: accentColor,
                mb: 2, 
                display: 'flex', 
                alignItems: 'center',
                fontSize: '1.25rem'
              }}>
                <CustomTimelineDot icon={SchoolIcon} color={accentColor} />
                <Box component="span" sx={{ 
                  ml: 1, 
                  textTransform: 'uppercase', 
                  fontSize: '1.2rem' 
                }}>
                  EDUCATION
                </Box>
              </Typography>

              <Timeline sx={{ p: 0 }}>
                {data.education.map((item, index) => (
                  <TimelineItem key={index} sx={{ '&::before': { flex: 0 } }}>
                    <TimelineSeparator>
                      <TimelineDot variant="outlined" sx={{ borderColor: accentColor }} />
                      {index < data.education.length - 1 && <TimelineConnector sx={{ bgcolor: accentColor }} />}
                    </TimelineSeparator>
                    <TimelineContent sx={{ pt: 0, pb: 3, ml: -1 }}>
                      <Typography variant="body2" sx={{ 
                        fontWeight: 'bold',
                        fontSize: '0.875rem'
                      }}>
                        {item.year}
                      </Typography>
                      <Typography variant="subtitle1" sx={{ 
                        fontWeight: 'bold', 
                        color: accentColor,
                        mt: 0.5, 
                        fontSize: '1rem' 
                      }}>
                        {item.degree}
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        fontWeight: 'bold', 
                        color: textColor,
                        fontSize: '0.9rem' 
                      }}>
                        {item.institution}
                      </Typography>
                      <Typography variant="caption" sx={{ 
                        color: textColor,
                        display: 'block',
                        fontSize: '0.8rem'
                      }}>
                        {item.details}
                      </Typography>
                      <Typography variant="caption" sx={{ 
                        display: 'block',
                        fontSize: '0.8rem'
                      }}>
                        GPA: {item.gpa}
                      </Typography>
                    </TimelineContent>
                  </TimelineItem>
                ))}
              </Timeline>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Experience */}
            <Box>
              <Typography variant="h5" sx={{ 
                fontWeight: 'bold', 
                color: accentColor,
                mb: 2, 
                display: 'flex', 
                alignItems: 'center',
                fontSize: '1.25rem'
              }}>
                <CustomTimelineDot icon={WorkIcon} color={accentColor} />
                <Box component="span" sx={{ 
                  ml: 1, 
                  textTransform: 'uppercase', 
                  fontSize: '1.2rem' 
                }}>
                  EXPERIENCE
                </Box>
              </Typography>

              <Timeline sx={{ p: 0 }}>
                {data.experience.map((item, index) => (
                  <TimelineItem key={index} sx={{ '&::before': { flex: 0 } }}>
                    <TimelineSeparator>
                      <TimelineDot variant="outlined" sx={{ borderColor: accentColor }} />
                      {index < data.experience.length - 1 && <TimelineConnector sx={{ bgcolor: accentColor }} />}
                    </TimelineSeparator>
                    <TimelineContent sx={{ pt: 0, pb: 3, ml: -1 }}>
                      <Typography variant="body2" sx={{ 
                        fontWeight: 'bold',
                        fontSize: '0.875rem'
                      }}>
                        {item.yearStart} - {item.year}
                      </Typography>
                      <Typography variant="subtitle1" sx={{ 
                        fontWeight: 'bold', 
                        color: accentColor,
                        mt: 0.5, 
                        fontSize: '1rem' 
                      }}>
                        {item.title}
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        fontWeight: 'bold', 
                        color: textColor,
                        fontSize: '0.9rem',
                        mb: 1 
                      }}>
                        {item.company}
                      </Typography>
                      
                      {/* Bullet Points */}
                      <List dense disablePadding sx={{ ml: -2 }}>
                        {item.bulletPoints.map((point, i) => (
                          <ListItem key={i} sx={{ py: 0.1, alignItems: 'flex-start' }}>
                            <CircleIcon sx={{ 
                              fontSize: 6, 
                              mr: 1, 
                              mt: 0.8, 
                              color: accentColor
                            }} />
                            <ListItemText 
                              primary={point} 
                              primaryTypographyProps={{ 
                                fontSize: '0.85rem' 
                              }} 
                            />
                          </ListItem>
                        ))}
                      </List>
                    </TimelineContent>
                  </TimelineItem>
                ))}
              </Timeline>
            </Box>

          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Resume23;

export const resumeMeta = {
  hasPhoto: true,
  columns: 2,
  colorOptions: [
    { value: '#e91e63', label: 'Pink' },
    { value: '#00bcd4', label: 'Cyan' },
    { value: '#8bc34a', label: 'Light Green' },
    { value: '#ff5722', label: 'Deep Orange' },
    { value: '#607d8b', label: 'Blue Grey' }
  ]
};